import React, { useEffect, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import jsVectorMap from 'jsvectormap';
import 'jsvectormap/dist/maps/world.js';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import moment from 'moment';
import 'bootstrap-daterangepicker/daterangepicker.css';
import { destroyScrollbars, initializeScrollbars } from './../../composables/scrollBar.js';

const dateRangePresets = {
	Today: [moment(), moment()],
	Yesterday: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
	'Last 7 Days': [moment().subtract(6, 'days'), moment()],
	'Last 30 Days': [moment().subtract(29, 'days'), moment()],
	'This Month': [moment().startOf('month'), moment().endOf('month')],
	'Last Month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')]
};

function DashboardDateRangeFilter() {
	const [dateRange, setDateRange] = useState(() => ({
		start: moment().subtract(7, 'days'),
		end: moment()
	}));
	const rangeGap = dateRange.end.diff(dateRange.start, 'days');
	const previousStart = moment(dateRange.start).subtract(rangeGap, 'days');
	const previousEnd = moment(dateRange.start).subtract(1, 'days');
	const settings = {
		startDate: dateRange.start,
		endDate: dateRange.end,
		maxSpan: { days: 60 },
		showDropdowns: true,
		parentEl: 'body',
		ranges: dateRangePresets,
		opens: 'right',
		drops: 'down',
		buttonClasses: ['btn', 'btn-sm'],
		applyButtonClasses: 'btn-primary',
		cancelButtonClasses: 'btn-default',
		locale: {
			applyLabel: 'Submit',
			cancelLabel: 'Cancel',
			customRangeLabel: 'Custom',
			daysOfWeek: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
			monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
			firstDay: 1
		}
	};

	return (
		<>
			<DateRangePicker initialSettings={settings} onCallback={(start, end) => setDateRange({ start, end })}>
				<a href="#/" className="btn btn-dark d-inline-flex gap-2 align-items-center me-2 text-truncate mb-2 mb-lg-0" id="daterange-filter">
					<iconify-icon icon="solar:calendar-bold-duotone" className="fa-fw fa-lg"></iconify-icon>
					<span>{dateRange.start.format('D MMMM YYYY')} - {dateRange.end.format('D MMMM YYYY')}</span>
					<b className="caret ms-1 opacity-5"></b>
				</a>
			</DateRangePicker>
			<div className="text-secondary fw-semibold">compared to <span id="daterange-prev-date">{previousStart.format('D MMMM')} - {previousEnd.format('D MMMM YYYY')}</span></div>
		</>
	);
}

function cssVariable(name, fallback) {
	const value = getComputedStyle(document.body).getPropertyValue(name).trim();
	return value || fallback;
}

function elementCssVariable(element, name, fallback) {
	const value = element ? getComputedStyle(element).getPropertyValue(name).trim() : '';
	return value || cssVariable(name, fallback);
}

function elementTheme(element) {
	const themedElement = element && element.closest('[data-bs-theme]');
	return (
		(themedElement && themedElement.getAttribute('data-bs-theme')) ||
		document.documentElement.getAttribute('data-bs-theme') ||
		'light'
	);
}

function chartColors() {
	return {
		primary: cssVariable('--bs-primary', '#348fe2'),
		dark: cssVariable('--bs-dark', '#212529'),
		blue: cssVariable('--bs-blue', '#348fe2'),
		teal: cssVariable('--bs-teal', '#00acac'),
		indigo: cssVariable('--bs-indigo', '#8753de'),
		orange: cssVariable('--bs-orange', '#f59c1a'),
		red: cssVariable('--bs-red', '#ff5b57'),
		lime: cssVariable('--bs-lime', '#90ca4b'),
		cyan: cssVariable('--bs-cyan', '#49b6d6'),
		green: cssVariable('--bs-green', '#198754'),
		gray: cssVariable('--bs-gray', '#6c757d'),
		gray200: cssVariable('--bs-gray-200', '#e9ecef'),
		gray400: cssVariable('--bs-gray-400', '#ced4da'),
		gray500: cssVariable('--bs-gray-500', '#adb5bd'),
		gray600: cssVariable('--bs-gray-600', '#6c757d'),
		body: cssVariable('--bs-body-color', '#6c757d'),
		border: cssVariable('--bs-border-color', 'rgba(0, 0, 0, .1)'),
		componentBg: cssVariable('--bs-component-bg', '#fff'),
		componentColor: cssVariable('--bs-component-color', '#212529'),
		componentColorRgb: cssVariable('--bs-component-color-rgb', '33, 37, 41'),
		primaryRgb: cssVariable('--bs-primary-rgb', '52, 143, 226'),
		white: cssVariable('--bs-white', '#fff')
	};
}

function renderChart(root, id, charts) {
	const container = root.querySelector('#' + id);
	const definition = charts[id];
	if (!container || !definition) return null;

	const baseOptions = definition.options || {
		chart: {
			type: definition.type,
			height: '100%',
			width: '100%',
			toolbar: { show: false },
			zoom: { enabled: false },
			animations: { enabled: true }
		},
		series: definition.series,
		colors: definition.colors,
		labels: definition.labels,
		dataLabels: { enabled: false },
		legend: definition.legend || { show: false },
		stroke: definition.stroke || { curve: 'smooth', width: 2 },
		fill: definition.fill || { opacity: 0.35 },
		grid: { borderColor: definition.gridColor || 'rgba(255,255,255,.15)' },
		xaxis: definition.xaxis || { labels: { style: { colors: '#8c96a0' } } },
		yaxis: definition.yaxis || { labels: { style: { colors: '#8c96a0' } } },
		plotOptions: definition.plotOptions,
		tooltip: { theme: 'dark' }
	};
	const chart = new ApexCharts(container, {
		...baseOptions,
		chart: {
			...(baseOptions.chart || {}),
			type: (baseOptions.chart && baseOptions.chart.type) || definition.type,
			height: (baseOptions.chart && baseOptions.chart.height) || '100%',
			width: (baseOptions.chart && baseOptions.chart.width) || '100%'
		},
		plotOptions: { line: {}, ...(baseOptions.plotOptions || {}) }
	});
	chart.render();
	return chart;
}

function renderMap(root, id, dark = false) {
	const map = root.querySelector('#' + id);
	if (!map) return null;

	if (map.__dashboardV3Map && map.__dashboardV3Map.destroy) {
		map.__dashboardV3Map.destroy();
	}
	map.innerHTML = '';

	const theme = cssVariable('--bs-app-theme', '#348fe2');
	const gray = cssVariable(dark ? '--bs-gray-700' : '--bs-gray-500', dark ? '#495057' : '#adb5bd');
	const background = dark ? cssVariable('--bs-gray-900', '#212529') : 'transparent';
	const mapInstance = new jsVectorMap({
		selector: '#' + id,
		map: 'world',
		zoomButtons: !dark,
		zoomOnScroll: false,
		hoverOpacity: 0.5,
		hoverColor: false,
		focusOn: { x: 0.5, y: 0.5, scale: 1 },
		markers: [
			{ name: 'Egypt', coords: [26.8206, 30.8025] },
			{ name: 'Russia', coords: [61.524, 105.3188] },
			{ name: 'Canada', coords: [56.1304, -106.3468] },
			{ name: 'Brazil', coords: [-14.235, -51.9253] }
		],
		markerStyle: { initial: { fill: theme, stroke: 'none', r: 5 }, hover: { fill: theme } },
		regionStyle: { initial: { fill: gray, fillOpacity: 0.5, stroke: 'none' }, hover: { fillOpacity: 0.8 } },
		backgroundColor: background
	});
	const destroy = () => {
		mapInstance.destroy();
		if (map.__dashboardV3Map && map.__dashboardV3Map.instance === mapInstance) {
			delete map.__dashboardV3Map;
			map.innerHTML = '';
		}
	};
	map.__dashboardV3Map = { destroy, instance: mapInstance };
	return { destroy };
}

function animateReferenceNumbers(root) {
	root.querySelectorAll('[data-animation="number"][data-value]').forEach((element) => {
		const value = Number(element.getAttribute('data-value'));
		if (!Number.isNaN(value)) {
			element.textContent = value.toLocaleString(undefined, {
				minimumFractionDigits: value % 1 ? 2 : 0,
				maximumFractionDigits: 2
			});
		}
	});
}

function animateReferenceProgress(root) {
	root.querySelectorAll('[data-animation="width"][data-value]').forEach((element) => {
		const value = element.getAttribute('data-value');
		const numericValue = value.replace('%', '');
		element.setAttribute('role', 'progressbar');
		element.setAttribute('aria-valuemin', '0');
		element.setAttribute('aria-valuemax', '100');
		element.setAttribute('aria-valuenow', numericValue);
		element.style.width = '0%';
		const applyWidth = () => {
			element.style.width = value;
		};
		window.requestAnimationFrame(applyWidth);
		window.setTimeout(applyWidth, 50);
	});
}

function getCharts(colors, root) {
	const offsets = Array.from({ length: 77 }, (_, index) => (index === 3 ? 73 : 77 - index));
	const pageOffsets = offsets.slice(0, -2).concat([2, 0]);
	const dateSeries = (values, seriesOffsets = offsets) =>
		values.map((value, index) => {
			const date = new Date();
			date.setDate(date.getDate() - seriesOffsets[index]);
			return [date.getTime(), value];
		});
	const uniqueVisitors = [
		13, 13, 6, 6, 6, 5, 5, 5, 6, 7, 6, 9, 9, 8, 10, 10, 10, 10, 9, 9, 10, 9, 9, 8, 8, 8, 8, 8, 7, 7, 6, 6, 6, 6, 5,
		5, 4, 4, 5, 5, 5, 7, 7, 7, 10, 9, 9, 10, 11, 11, 8, 8, 7, 8, 9, 8, 9, 10, 9, 10, 16, 17, 16, 17, 16, 15, 14, 24,
		18, 15, 14, 16, 16, 17, 7, 7, 7
	];
	const pageViews = [
		14, 13, 15, 14, 13, 15, 16, 16, 14, 14, 13, 12, 13, 13, 15, 16, 16, 17, 17, 18, 15, 15, 15, 19, 19, 18, 18, 17,
		16, 18, 18, 18, 16, 14, 14, 13, 14, 13, 10, 9, 10, 11, 11, 11, 10, 9, 10, 13, 14, 14, 13, 12, 11, 13, 13, 13,
		13, 14, 13, 13, 19, 21, 22, 25, 24, 24, 22, 16, 15, 12, 12, 15, 15, 15, 18, 18, 17
	];
	const visitorSeries = [
		{ name: 'Unique Visitors', data: dateSeries(uniqueVisitors) },
		{ name: 'Page Views', data: dateSeries(pageViews, pageOffsets) }
	];
	const charts = {};

	const v3Theme = (id) => elementTheme(root.querySelector('#' + id));
	const responsive = (widths) => widths.map(([breakpoint, width]) => ({ breakpoint, options: { chart: { width } } }));
	const spark = (id, data, colorStops, formatter, width, height, stacked, widths) => ({
		type: 'line',
		options: {
			chart: {
				type: 'line',
				width,
				height,
				sparkline: { enabled: true },
				...(stacked === undefined ? {} : { stacked })
			},
			stroke: { curve: 'smooth', width: 3 },
			fill: { type: 'gradient', gradient: { opacityFrom: 1, opacityTo: 1, colorStops } },
			series: [{ data }],
			labels: ['Jun 23', 'Jun 24', 'Jun 25', 'Jun 26', 'Jun 27', 'Jun 28', 'Jun 29'],
			xaxis: { crosshairs: { width: 1 } },
			tooltip: {
				theme: v3Theme(id),
				fixed: { enabled: false },
				x: { show: false },
				y: { title: { formatter: () => '' }, formatter },
				marker: { show: false }
			},
			responsive: responsive(widths)
		}
	});
	const visitorElement = root.querySelector('#visitorLineChart');
	const visitorBodyColor = elementCssVariable(visitorElement, '--bs-body-color', colors.body);
	const visitorBorderColor = elementCssVariable(visitorElement, '--bs-border-color', colors.border);
	charts.visitorLineChart = {
		type: 'area',
		options: {
			series: visitorSeries,
			colors: [
				elementCssVariable(visitorElement, '--bs-teal', colors.teal),
				elementCssVariable(visitorElement, '--bs-blue', colors.blue)
			],
			fill: { opacity: 0.75, type: 'solid' },
			legend: {
				position: 'top',
				horizontalAlign: 'right',
				offsetY: 15,
				offsetX: 500,
				labels: { colors: visitorBodyColor }
			},
			xaxis: { type: 'datetime', tickAmount: 6, labels: { style: { colors: visitorBodyColor } } },
			yaxis: { labels: { style: { colors: visitorBodyColor } } },
			tooltip: { theme: v3Theme('visitorLineChart'), y: { formatter: (value) => '$ ' + value + ' thousands' } },
			chart: { height: '100%', type: 'area', toolbar: { show: false }, stacked: true, zoom: { enabled: false } },
			plotOptions: { bar: { horizontal: false, columnWidth: '55%', endingShape: 'rounded' } },
			dataLabels: { enabled: false },
			grid: {
				show: true,
				borderColor: visitorBorderColor,
				xaxis: { lines: { show: true } },
				yaxis: { lines: { show: true } },
				padding: { top: -40, right: 3, bottom: 0, left: 10 }
			},
			stroke: { show: false, curve: 'straight' }
		}
	};
	charts['total-sales-sparkline'] = spark(
		'total-sales-sparkline',
		[9452.37, 11018.87, 7296.37, 6274.29, 7924.05, 6581.34, 12918.14],
		[
			{ offset: 0, color: colors.blue, opacity: 1 },
			{ offset: 100, color: colors.indigo, opacity: 1 }
		],
		(value) => '$' + Number(value).toLocaleString(),
		200,
		36,
		true,
		[
			[3000, 130],
			[1300, 100],
			[1200, 200],
			[576, 180],
			[400, 120]
		]
	);
	charts['conversion-rate-sparkline'] = spark(
		'conversion-rate-sparkline',
		[2.68, 2.93, 2.04, 1.61, 1.88, 1.62, 2.8],
		[
			{ offset: 0, color: colors.red, opacity: 1 },
			{ offset: 50, color: colors.orange, opacity: 1 },
			{ offset: 100, color: colors.lime, opacity: 1 }
		],
		(value) => value + '%',
		160,
		28,
		undefined,
		[
			[3000, 120],
			[1300, 100],
			[1200, 160],
			[900, 120],
			[576, 180],
			[400, 120]
		]
	);
	charts['store-session-sparkline'] = spark(
		'store-session-sparkline',
		[10812, 11393, 7311, 6834, 9612, 11200, 13557],
		[
			{ offset: 0, color: colors.teal, opacity: 1 },
			{ offset: 50, color: colors.blue, opacity: 1 },
			{ offset: 100, color: colors.cyan, opacity: 1 }
		],
		(value) => Number(value).toLocaleString(),
		160,
		28,
		false,
		[
			[3000, 120],
			[1300, 100],
			[1200, 160],
			[900, 120],
			[576, 180],
			[400, 120]
		]
	);

	return charts;
}

function wireActions(root) {
	root.querySelectorAll('a[href="#/"]').forEach((link) => {
		link.addEventListener('click', (event) => event.preventDefault());
	});

	const exportButton = root.querySelector('#dashboard-export');
	if (exportButton) {
		exportButton.addEventListener('click', (event) => {
			event.preventDefault();
			const csv = 'Metric,Value\nTotal Sales,128940.55\nOrders,3824\nConversion,5.12%\n';
			const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
			const link = document.createElement('a');
			link.href = url;
			link.download = 'dashboard-export.csv';
			link.click();
			URL.revokeObjectURL(url);
		});
	}

}

function DashboardV3() {
	const rootRef = useRef(null);
	const [themeVersion, setThemeVersion] = useState(0);

	useEffect(() => {
		if (!rootRef.current) return undefined;
		const root = rootRef.current;
		const colors = chartColors();
		const charts = [];
		const maps = [renderMap(root, 'visitorMap')];
		const definitions = getCharts(colors, root);

		animateReferenceNumbers(root);
		animateReferenceProgress(root);
		Object.keys(definitions).forEach((id) => {
			const chart = renderChart(root, id, definitions);
			if (chart) charts.push(chart);
		});
		const scrollbars = initializeScrollbars(root);
		wireActions(root);

		const refresh = () => setThemeVersion((current) => current + 1);
		document.addEventListener('theme-reload', refresh);
		return () => {
			document.removeEventListener('theme-reload', refresh);
			charts.forEach((chart) => chart.destroy());
			maps.filter(Boolean).forEach((map) => map.destroy && map.destroy());
		destroyScrollbars(scrollbars);
		};
	}, [themeVersion]);

	return (
		<div ref={rootRef}>
			<ol className="breadcrumb float-xl-end">
				<li className="breadcrumb-item">
					<a href="#/">Home</a>
				</li>
				<li className="breadcrumb-item">
					<a href="#/">Dashboard</a>
				</li>
				<li className="breadcrumb-item active">Dashboard v3</li>
			</ol>
			<h1 className="page-header mb-10px">Dashboard v3</h1>
			<div className="d-lg-flex align-items-center mb-3 gap-1">
				<DashboardDateRangeFilter />
			</div>
			<div className="row g-3">
				<div className="col-xl-6">
					<div className="card border-0 overflow-hidden rounded-3 h-100" data-bs-theme="dark">
						<div className="card-body">
							<div className="row gx-4">
								<div className="col-xl-7 col-lg-8">
									<div className="mb-3 d-flex align-items-center">
										<div className="fw-bold">Total Sales</div>
										<span className="ms-2 text-body text-opacity-25">
											<i
												className="fa fa-info-circle"
												data-bs-toggle="popover"
												data-bs-trigger="hover"
												data-bs-title="Total sales"
												data-bs-placement="top"
												data-bs-content="Net sales (gross sales minus discounts and returns) plus taxes and shipping. Includes orders from all sales channels."
											></i>
										</span>
									</div>
									<div className="d-flex mb-1">
										<div className="h2 mb-0">
											$
											<span data-animation="number" data-value="64559.25">
												0.00
											</span>
										</div>
										<div className="ms-auto my-n1">
											<div id="total-sales-sparkline"></div>
										</div>
									</div>
									<div className="small fw-semibold text-body text-opacity-75">
										▲{' '}
										<span data-animation="number" data-value="33.21">
											0.00
										</span>
										% compare to last week
									</div>
									<hr className="text-body text-opacity-50 my-3" />
									<div className="row text-truncate small">
										<div className="col-6">
											<div className="text-body fw-semibold">Total sales order</div>
											<div
												className="fs-5 fw-bold mb-10px text-white"
												data-animation="number"
												data-value="1568"
											>
												0
											</div>
											<div className="progress h-6px rounded-3 bg-light">
												<div
													className="progress-bar progress-bar-striped rounded-right bg-teal"
													data-animation="width"
													data-value="55%"
													style={{ width: '0%' }}
												></div>
											</div>
										</div>
										<div className="col-6">
											<div className="text-body fw-semibold">Avg. sales per order</div>
											<div className="fs-5 fw-bold mb-10px text-white">
												$
												<span data-animation="number" data-value="41.20">
													0.00
												</span>
											</div>
											<div className="progress h-6px rounded-3 bg-light">
												<div
													className="progress-bar progress-bar-striped rounded-right bg-primary"
													data-animation="width"
													data-value="55%"
													style={{ width: '0%' }}
												></div>
											</div>
										</div>
									</div>
								</div>
								<div className="col-xl-5 col-lg-4 align-items-center d-flex justify-content-center">
									<img src="/color-admin/admin/react/assets/img/svg/img-1.svg" alt="" className="d-none d-lg-block w-100" />
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="col-xl-6">
					<div className="row h-100">
						<div className="col-sm-6">
							<div className="card border-0 overflow-hidden rounded-3 h-100" data-bs-theme="dark">
								<div className="card-body">
									<div className="mb-3 d-flex align-items-center">
										<div className="fw-bold">Conversion Rate</div>
										<span className="ms-2 text-body text-opacity-25">
											<i
												className="fa fa-info-circle"
												data-bs-toggle="popover"
												data-bs-trigger="hover"
												data-bs-title="Conversion Rate"
												data-bs-placement="top"
												data-bs-content="Percentage of sessions that resulted in orders from total number of sessions."
												data-original-title=""
												title=""
											></i>
										</span>
									</div>
									<div className="d-flex align-items-center mb-1">
										<div className="h2 mb-0">
											<span data-animation="number" data-value="2.19">
												0.00
											</span>
											%
										</div>
										<div className="ms-auto my-n1">
											<div id="conversion-rate-sparkline"></div>
										</div>
									</div>
									<div className="small fw-semibold text-body text-opacity-75 mb-3">
										▼{' '}
										<span data-animation="number" data-value="0.50">
											0.00
										</span>
										% compare to last week
									</div>
									<hr className="text-body text-opacity-50 my-3" />
									<div className="d-flex flex-column gap-1 small text-white text-nowrap">
										<div className="d-flex gap-2">
											<div className="d-flex gap-2 align-items-center fw-semibold flex-1 overflow-hidden">
												<i className="fa fa-circle text-red fs-6px"></i>
												<span className="text-ellipsis">Added to cart</span>
											</div>
											<div className="d-flex align-items-center">
												<div className="text-body text-opacity-50 fw-semibold">
													▲{' '}
													<span data-animation="number" data-value="262">
														0
													</span>
													%
												</div>
												<div className="w-50px text-end fw-bold">
													<span data-animation="number" data-value="3.79">
														0.00
													</span>
													%
												</div>
											</div>
										</div>
										<div className="d-flex gap-2">
											<div className="d-flex gap-2 align-items-center fw-semibold flex-1 overflow-hidden">
												<i className="fa fa-circle text-warning fs-6px"></i>
												<span className="text-ellipsis">Reached checkout</span>
											</div>
											<div className="d-flex align-items-center">
												<div className="text-body text-opacity-50 fw-semibold">
													▲{' '}
													<span data-animation="number" data-value="11">
														0
													</span>
													%
												</div>
												<div className="w-50px text-end fw-bold">
													<span data-animation="number" data-value="3.85">
														0.00
													</span>
													%
												</div>
											</div>
										</div>
										<div className="d-flex">
											<div className="d-flex gap-2 align-items-center fw-semibold flex-1 overflow-hidden">
												<i className="fa fa-circle text-lime fs-6px"></i>
												<span className="text-ellipsis">Sessions converted</span>
											</div>
											<div className="d-flex align-items-center">
												<div className="text-body text-opacity-50 fw-semibold">
													▲{' '}
													<span data-animation="number" data-value="57">
														0
													</span>
													%
												</div>
												<div className="w-50px text-end fw-bold">
													<span data-animation="number" data-value="2.19">
														0.00
													</span>
													%
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="col-sm-6">
							<div className="card border-0 overflow-hidden rounded-3 h-100" data-bs-theme="dark">
								<div className="card-body">
									<div className="mb-3 d-flex align-items-center">
										<div className="fw-bold">Store Sessions</div>
										<span className="ms-2 text-body text-opacity-25">
											<i
												className="fa fa-info-circle"
												data-bs-toggle="popover"
												data-bs-trigger="hover"
												data-bs-title="Store Sessions"
												data-bs-placement="top"
												data-bs-content="Number of sessions on your online store. A session is a period of continuous activity from a visitor."
												data-original-title=""
												title=""
											></i>
										</span>
									</div>
									<div className="d-flex align-items-center mb-1">
										<div className="h2 mb-0">
											<span data-animation="number" data-value="70719">
												0
											</span>
										</div>
										<div className="ms-auto my-n1">
											<div id="store-session-sparkline"></div>
										</div>
									</div>
									<div className="small fw-semibold text-body text-opacity-75 mb-3">
										▲{' '}
										<span data-animation="number" data-value="9.5">
											0.00
										</span>
										% compare to last week
									</div>
									<hr className="text-body text-opacity-50 my-3" />
									<div className="d-flex flex-column gap-1 small text-white">
										<div className="d-flex gap-2">
											<div className="d-flex gap-2 align-items-center fw-semibold flex-1 overflow-hidden">
												<i className="fa fa-circle text-teal fs-6px"></i>
												<span className="text-ellipsis">Mobile</span>
											</div>
											<div className="d-flex align-items-center">
												<div className="text-body text-opacity-50 fw-semibold">
													▲{' '}
													<span data-animation="number" data-value="25.7">
														0.00
													</span>
													%
												</div>
												<div className="w-50px text-end fw-bold">
													<span data-animation="number" data-value="53210">
														0
													</span>
												</div>
											</div>
										</div>
										<div className="d-flex gap-2">
											<div className="d-flex gap-2 align-items-center fw-semibold flex-1 overflow-hidden">
												<i className="fa fa-circle text-blue fs-6px"></i>
												<span className="text-ellipsis">Desktop</span>
											</div>
											<div className="d-flex align-items-center">
												<div className="text-body text-opacity-50 fw-semibold">
													▲{' '}
													<span data-animation="number" data-value="16.0">
														0.00
													</span>
													%
												</div>
												<div className="w-50px text-end fw-bold">
													<span data-animation="number" data-value="11959">
														0
													</span>
												</div>
											</div>
										</div>
										<div className="d-flex gap-2">
											<div className="d-flex gap-2 align-items-center fw-semibold flex-1 overflow-hidden">
												<i className="fa fa-circle text-cyan fs-6px"></i>
												<span className="text-ellipsis">Tablet</span>
											</div>
											<div className="d-flex align-items-center">
												<div className="text-body text-opacity-50 fw-semibold">
													▲{' '}
													<span data-animation="number" data-value="7.9">
														0.00
													</span>
													%
												</div>
												<div className="w-50px text-end fw-bold">
													<span data-animation="number" data-value="5545">
														0
													</span>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="col-xl-8 col-lg-6">
					<div className="card border-0 overflow-hidden rounded-3 h-100" data-bs-theme="dark">
						<div className="card-body pb-0">
							<div className="mb-3 d-flex align-items-center">
								<div className="fw-bold">Visitor Analytics</div>
								<span className="ms-2 text-body text-opacity-25">
									<i
										className="fa fa-info-circle"
										data-bs-toggle="popover"
										data-bs-trigger="hover"
										data-bs-title="Top products with units sold"
										data-bs-placement="top"
										data-bs-content="Products with the most individual units sold. Includes orders from all sales channels."
										data-original-title=""
										title=""
									></i>
								</span>
							</div>
							<div className="row">
								<div className="col-xl-3 col-4">
									<div className="h2 mb-1 ">
										<span data-animation="number" data-value="127.1">
											0
										</span>
										K
									</div>
									<div className="small fw-semibold">New Visitors</div>
									<div className="small fw-semibold text-body text-opacity-75 text-truncate">
										▲{' '}
										<span data-animation="number" data-value="25.5">
											0.00
										</span>
										% from previous 7 days
									</div>
								</div>
								<div className="col-xl-3 col-4">
									<div className="h2 mb-1 ">
										<span data-animation="number" data-value="179.9">
											0
										</span>
										K
									</div>
									<div className="small fw-semibold">Returning Visitors</div>
									<div className="small fw-semibold text-body text-opacity-75 text-truncate">
										▲{' '}
										<span data-animation="number" data-value="5.33">
											0.00
										</span>
										% from previous 7 days
									</div>
								</div>
								<div className="col-xl-3 col-4">
									<div className="h2 mb-1 ">
										<span data-animation="number" data-value="766.8">
											0
										</span>
										K
									</div>
									<div className="small fw-semibold">Total Page Views</div>
									<div className="small fw-semibold text-body text-opacity-75 text-truncate">
										▲{' '}
										<span data-animation="number" data-value="0.323">
											0.00
										</span>
										% from previous 7 days
									</div>
								</div>
							</div>
						</div>
						<div className="card-body p-0 pe-3 pb-2" style={{ height: '280px' }}>
							<div id="visitorLineChart"></div>
						</div>
					</div>
				</div>
				<div className="col-xl-4 col-lg-6">
					<div className="card border-0 overflow-hidden rounded-3 h-100" data-bs-theme="dark">
						<div className="card-body bg-light">
							<div className="mb-3 d-flex align-items-center">
								<div className="fw-bold">Session by Location</div>
								<span className="ms-2 text-body text-opacity-25">
									<i
										className="fa fa-info-circle"
										data-bs-toggle="popover"
										data-bs-trigger="hover"
										data-bs-title="Total sales"
										data-bs-placement="top"
										data-bs-content="Net sales (gross sales minus discounts and returns) plus taxes and shipping. Includes orders from all sales channels."
									></i>
								</span>
							</div>
							<div id="visitorMap" className="mb-n3" style={{ height: '200px' }}></div>
						</div>
						<div className="card-body">
							<div className="d-flex flex-column gap-2 fw-semibold">
								<div className="d-flex align-items-center gap-3">
									<div className="w-35px h-25px rounded-1 overflow-hidden d-flex align-items-center justify-content-center">
										<iconify-icon icon="flag:us-4x3" className="fs-2"></iconify-icon>
									</div>
									<div className="d-flex w-100">
										<div className="flex-1 text-white">United States</div>
										<div className="">
											<span data-animation="number" data-value="39.85">
												0.00
											</span>
											%
										</div>
									</div>
								</div>
								<div className="d-flex align-items-center gap-3">
									<div className="w-35px h-25px rounded-1 overflow-hidden d-flex align-items-center justify-content-center">
										<iconify-icon icon="flag:cn-4x3" className="fs-2"></iconify-icon>
									</div>
									<div className="d-flex w-100">
										<div className="flex-1 text-white">China</div>
										<div className="">
											<span data-animation="number" data-value="14.23">
												0.00
											</span>
											%
										</div>
									</div>
								</div>
								<div className="d-flex align-items-center gap-3">
									<div className="w-35px h-25px rounded-1 overflow-hidden d-flex align-items-center justify-content-center">
										<iconify-icon icon="flag:de-4x3" className="fs-2"></iconify-icon>
									</div>
									<div className="d-flex w-100">
										<div className="flex-1 text-white">Germany</div>
										<div className="">
											<span data-animation="number" data-value="12.83">
												0.00
											</span>
											%
										</div>
									</div>
								</div>
								<div className="d-flex align-items-center gap-3">
									<div className="w-35px h-25px rounded-1 overflow-hidden d-flex align-items-center justify-content-center">
										<iconify-icon icon="flag:fr-4x3" className="fs-2"></iconify-icon>
									</div>
									<div className="d-flex w-100">
										<div className="flex-1 text-white">France</div>
										<div className="">
											<span data-animation="number" data-value="11.14">
												0.00
											</span>
											%
										</div>
									</div>
								</div>
								<div className="d-flex align-items-center gap-3">
									<div className="w-35px h-25px rounded-1 overflow-hidden d-flex align-items-center justify-content-center">
										<iconify-icon icon="flag:jp-4x3" className="fs-2"></iconify-icon>
									</div>
									<div className="d-flex w-100">
										<div className="flex-1 text-white">Japan</div>
										<div className="">
											<span data-animation="number" data-value="10.75">
												0.00
											</span>
											%
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="col-xl-4 col-lg-6">
					<div className="card border-0 overflow-hidden rounded-3 h-100" data-bs-theme="dark">
						<div
							className="card-body bg-gray-900"
							style={{
								background: 'no-repeat bottom right',
								backgroundImage: 'url(/color-admin/admin/react/assets/img/svg/img-4.svg)',
								backgroundSize: 'auto 60%'
							}}
						>
							<div className="mb-3 d-flex align-items-center">
								<div className="fw-bold">Sales by Social Source</div>
								<span className="ms-2 text-body text-opacity-25">
									<i
										className="fa fa-info-circle"
										data-bs-toggle="popover"
										data-bs-trigger="hover"
										data-bs-title="Sales by social source"
										data-bs-placement="top"
										data-bs-content="Total online store sales that came from a social referrer source."
									></i>
								</span>
							</div>
							<h2 className="mb-1">
								$
								<span data-animation="number" data-value="55547.89">
									0.00
								</span>
							</h2>
							<div className="small fw-semibold text-body text-opacity-75">
								<i className="fa fa-caret-up"></i>{' '}
								<span data-animation="number" data-value="45.76">
									0.00
								</span>
								% increased
							</div>
						</div>
						<div className="py-3 fw-semibold">
							<div className="d-flex align-items-center gap-3 ps-3">
								<div className="w-30px h-30px rounded bg-indigo text-white d-flex align-items-center justify-content-center">
									<i className="fab fa-apple fs-5"></i>
								</div>
								<div className="flex-1 d-flex gap-3">
									<div className="flex-1 text-white">Apple Store</div>
									<div className="text-body pe-3">
										$
										<span data-animation="number" data-value="34840.17">
											0.00
										</span>
									</div>
								</div>
							</div>
							<div className="d-flex gap-3 ps-3 my-2">
								<div className="w-30px"></div>
								<div className="flex-1">
									<hr className="m-0 text-body text-opacity-25" />
								</div>
							</div>
							<div className="d-flex align-items-center gap-3 ps-3">
								<div className="w-30px h-30px rounded bg-blue text-white d-flex align-items-center justify-content-center">
									<i className="fab fa-facebook-f"></i>
								</div>
								<div className="flex-1 d-flex gap-3">
									<div className="flex-1 text-white">Facebook</div>
									<div className="text-body pe-3">
										$
										<span data-animation="number" data-value="12502.67">
											0.00
										</span>
									</div>
								</div>
							</div>
							<div className="d-flex gap-3 ps-3 my-2">
								<div className="w-30px"></div>
								<div className="flex-1">
									<hr className="m-0 text-body text-opacity-25" />
								</div>
							</div>
							<div className="d-flex align-items-center gap-3 ps-3">
								<div className="w-30px h-30px rounded bg-info text-white d-flex align-items-center justify-content-center">
									<i className="fab fa-twitter"></i>
								</div>
								<div className="flex-1 d-flex gap-3">
									<div className="flex-1 text-white">Twitter</div>
									<div className="text-body pe-3">
										$
										<span data-animation="number" data-value="4799.21">
											0.00
										</span>
									</div>
								</div>
							</div>
							<div className="d-flex gap-3 ps-3 my-2">
								<div className="w-30px"></div>
								<div className="flex-1">
									<hr className="m-0 text-body text-opacity-25" />
								</div>
							</div>
							<div className="d-flex align-items-center gap-3 ps-3">
								<div className="w-30px h-30px rounded bg-red text-white d-flex align-items-center justify-content-center">
									<i className="fab fa-google"></i>
								</div>
								<div className="flex-1 d-flex gap-3">
									<div className="flex-1 text-white">Google Adwords</div>
									<div className="text-body pe-3">
										$
										<span data-animation="number" data-value="3405.85">
											0.00
										</span>
									</div>
								</div>
							</div>
							<div className="d-flex gap-3 ps-3 my-2">
								<div className="w-30px"></div>
								<div className="flex-1">
									<hr className="m-0 text-body text-opacity-25" />
								</div>
							</div>
							<div className="d-flex align-items-center gap-3 ps-3">
								<div className="w-30px h-30px rounded bg-pink text-white d-flex align-items-center justify-content-center">
									<i className="fab fa-google"></i>
								</div>
								<div className="flex-1 d-flex gap-3">
									<div className="flex-1 text-white">Instagram</div>
									<div className="text-body pe-3">
										$
										<span data-animation="number" data-value="1311.64">
											0.00
										</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="col-xl-4 col-lg-6">
					<div className="card border-0 overflow-hidden rounded-3 h-100" data-bs-theme="dark">
						<div className="card-body">
							<div className="mb-3 d-flex align-items-center">
								<div className="fw-bold">Top Products by Units Sold</div>
								<span className="ms-2 text-body text-opacity-25">
									<i
										className="fa fa-info-circle"
										data-bs-toggle="popover"
										data-bs-trigger="hover"
										data-bs-title="Top products with units sold"
										data-bs-placement="top"
										data-bs-content="Products with the most individual units sold. Includes orders from all sales channels."
									></i>
								</span>
							</div>
							<div className="me-n3">
								<div className="d-flex gap-3 align-items-center pe-3">
<img
										src="/color-admin/admin/react/assets/img/product/iphone-17-pro-max.jpg"
										className="w-50px h-50px rounded-3 object-fit-cover"
									 alt="" />
									<div className="flex-1 d-flex gap-3">
										<div className="flex-1">
											<div className="fw-semibold text-white">iPhone 17 Pro Max</div>
											<div className="fw-semibold text-body text-opacity-75 small">$1,099</div>
										</div>
										<div className="text-end">
											<div className="fw-semibold text-white">
												<span data-animation="number" data-value="195">
													0
												</span>
											</div>
											<div className="fw-semibold text-body text-opacity-75 small">sold</div>
										</div>
									</div>
								</div>
								<div className="d-flex gap-3">
									<div className="w-50px"></div>
									<div className="flex-1">
										<hr className="text-body text-opacity-25 my-2" />
									</div>
								</div>
								<div className="d-flex gap-3 align-items-center pe-3">
<img
										src="/color-admin/admin/react/assets/img/product/ipad-pro.jpg"
										className="w-50px h-50px rounded-3 object-fit-cover"
									 alt="" />
									<div className="flex-1 d-flex gap-3">
										<div className="flex-1">
											<div className="fw-semibold text-white">iPad Pro 2026</div>
											<div className="fw-semibold text-body text-opacity-75 small">$999</div>
										</div>
										<div className="text-end">
											<div className="fw-semibold text-white">
												<span data-animation="number" data-value="185">
													0
												</span>
											</div>
											<div className="fw-semibold text-body text-opacity-75 small">sold</div>
										</div>
									</div>
								</div>
								<div className="d-flex gap-3">
									<div className="w-50px"></div>
									<div className="flex-1">
										<hr className="text-body text-opacity-25 my-2" />
									</div>
								</div>
								<div className="d-flex gap-3 align-items-center pe-3">
<img
										src="/color-admin/admin/react/assets/img/product/apple-watch-ultra-3.jpg"
										className="w-50px h-50px rounded-3 object-fit-cover"
									 alt="" />
									<div className="flex-1 d-flex gap-3">
										<div className="flex-1">
											<div className="fw-semibold text-white">Apple Watch Ultra 3</div>
											<div className="fw-semibold text-body text-opacity-75 small">$799</div>
										</div>
										<div className="text-end">
											<div className="fw-semibold text-white">
												<span data-animation="number" data-value="129">
													0
												</span>
											</div>
											<div className="fw-semibold text-body text-opacity-75 small">sold</div>
										</div>
									</div>
								</div>
								<div className="d-flex gap-3">
									<div className="w-50px"></div>
									<div className="flex-1">
										<hr className="text-body text-opacity-25 my-2" />
									</div>
								</div>
								<div className="d-flex gap-3 align-items-center pe-3">
<img
										src="/color-admin/admin/react/assets/img/product/ipad-air.jpg"
										className="w-50px h-50px rounded-3 object-fit-cover"
									 alt="" />
									<div className="flex-1 d-flex gap-3">
										<div className="flex-1">
											<div className="fw-semibold text-white">iPad Air</div>
											<div className="fw-semibold text-body text-opacity-75 small">$599</div>
										</div>
										<div className="text-end">
											<div className="fw-semibold text-white">
												<span data-animation="number" data-value="96">
													0
												</span>
											</div>
											<div className="fw-semibold text-body text-opacity-75 small">sold</div>
										</div>
									</div>
								</div>
								<div className="d-flex gap-3">
									<div className="w-50px"></div>
									<div className="flex-1">
										<hr className="text-body text-opacity-25 my-2" />
									</div>
								</div>
								<div className="d-flex gap-3 align-items-center pe-3">
<img
										src="/color-admin/admin/react/assets/img/product/apple-watch-series-11.jpg"
										className="w-50px h-50px rounded-3 object-fit-cover"
									 alt="" />
									<div className="flex-1 d-flex gap-3">
										<div className="flex-1">
											<div className="fw-semibold text-white">Apple Watch Series 11</div>
											<div className="fw-semibold text-body text-opacity-75 small">$399</div>
										</div>
										<div className="text-end">
											<div className="fw-semibold text-white">
												<span data-animation="number" data-value="55">
													0
												</span>
											</div>
											<div className="fw-semibold text-body text-opacity-75 small">sold</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="col-xl-4 col-lg-6">
					<div className="card border-0 overflow-hidden rounded-3 h-100" data-bs-theme="dark">
						<div className="card-body">
							<div className="mb-3 d-flex align-items-center">
								<div className="fw-bold">Marketing Campaign</div>
								<span className="ms-2 text-body text-opacity-25">
									<i
										className="fa fa-info-circle"
										data-bs-toggle="popover"
										data-bs-trigger="hover"
										data-bs-title="Marketing Campaign"
										data-bs-placement="top"
										data-bs-content="Campaign that run for getting more returning customers."
									></i>
								</span>
							</div>
							<div className="d-flex align-items-center gap-3">
								<div
									className="w-150px p-3 rounded-3 bg-light d-flex align-items-center justify-content-center"
									style={{ height: '140px' }}
								>
									<img src="/color-admin/admin/react/assets/img/svg/img-2.svg" className="mw-100 mh-100"  alt="" />
								</div>
								<div className="flex-1 fw-semibold overflow-hidden py-2">
									<div className="text-white text-truncate">Email Marketing Campaign</div>
									<div className="small text-body text-opacity-75 mb-1">Mon 12/6 - Sun 18/6</div>
									<div className="d-flex align-items-center">
										<div className="flex-1">
											<div className="progress h-5px rounded-pill">
												<div
													className="progress-bar progress-bar-striped bg-indigo"
													data-animation="width"
													data-value="80%"
													style={{ width: '0%' }}
												></div>
											</div>
										</div>
										<div className="ms-2 small w-30px text-center">
											<span data-animation="number" data-value="80">
												0
											</span>
											%
										</div>
									</div>
									<div className="mb-2 text-truncate text-body text-opacity-50 fs-11px">
										57.5% people click the email
									</div>
									<a href="#/" className="btn btn-xs btn-indigo fs-10px px-2 rounded-1">
										View campaign
									</a>
								</div>
							</div>
							<hr className="text-body text-opacity-25" />
							<div className="d-flex align-items-center gap-3">
								<div
									className="w-150px p-3 rounded-3 bg-light d-flex align-items-center justify-content-center"
									style={{ height: '140px' }}
								>
									<img src="/color-admin/admin/react/assets/img/svg/img-3.svg" className="mw-100 mh-100"  alt="" />
								</div>
								<div className="flex-1 fw-semibold overflow-hidden py-2">
									<div className="text-white text-truncate">Facebook Marketing Campaign</div>
									<div className="small text-body text-opacity-75 mb-1">Sat 10/6 - Sun 18/6</div>
									<div className="d-flex align-items-center">
										<div className="flex-1">
											<div className="progress h-5px rounded-pill">
												<div
													className="progress-bar progress-bar-striped bg-warning"
													data-animation="width"
													data-value="80%"
													style={{ width: '0%' }}
												></div>
											</div>
										</div>
										<div className="ms-2 small w-30px text-center">
											<span data-animation="number" data-value="60">
												0
											</span>
											%
										</div>
									</div>
									<div className="mb-2 text-truncate text-body text-opacity-50 fs-11px">
										+124k visitors from facebook
									</div>
									<a href="#/" className="btn btn-xs btn-warning fs-10px px-2 rounded-1">
										View campaign
									</a>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default DashboardV3;
